/**
 * CEO Command Center - Logging & Tracing Service
 * 
 * Enterprise-grade logging system based on SaaS/AI best practices:
 * - Structured JSON logging with consistent schema
 * - Log levels: DEBUG, INFO, WARN, ERROR, FATAL
 * - Correlation IDs for tracking related events
 * - Performance tracing for async operations
 * - Breadcrumbs for error context
 * - Export to file for debugging
 * - Persistent storage (localStorage + IndexedDB fallback)
 * 
 * Inspired by: Winston, Sentry, OpenTelemetry, Datadog
 * 
 * @module services/logger
 * @version 10.3.0
 */

// ============================================
// TYPES
// ============================================

export type LogLevel = 'DEBUG' | 'INFO' | 'WARN' | 'ERROR' | 'FATAL';

export interface LogEntry {
  /** Unique log ID */
  id: string;
  
  /** ISO timestamp */
  timestamp: string;
  
  /** Log level */
  level: LogLevel;
  
  /** Log category/source */
  category: string;
  
  /** Human-readable message */
  message: string;
  
  /** Structured data */
  data?: Record<string, unknown>;
  
  /** Error details if applicable */
  error?: {
    name: string;
    message: string;
    stack?: string;
  };
  
  /** Correlation ID for tracking related events */
  correlationId?: string;
  
  /** Session ID */
  sessionId: string;
  
  /** User action that triggered this (if applicable) */
  userAction?: string;
  
  /** Duration in ms (for performance tracing) */
  duration?: number;
  
  /** Tags for filtering */
  tags?: string[];
}

export interface Breadcrumb {
  timestamp: string;
  category: string;
  message: string;
  data?: Record<string, unknown>;
}

export interface TraceSpan {
  id: string;
  name: string;
  category: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  correlationId: string;
  data?: Record<string, unknown>;
  status: 'pending' | 'success' | 'error';
  error?: string;
}

interface LoggerConfig {
  /** Minimum log level to capture */
  minLevel: LogLevel;
  
  /** Maximum logs to keep in memory */
  maxLogs: number;
  
  /** Maximum breadcrumbs to keep */
  maxBreadcrumbs: number;
  
  /** Enable console output */
  consoleOutput: boolean;
  
  /** Enable localStorage persistence */
  persistLogs: boolean;
  
  /** Auto-capture global errors */
  captureGlobalErrors: boolean;
  
  /** Auto-capture network requests */
  captureNetworkRequests: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const LOG_LEVELS: Record<LogLevel, number> = {
  DEBUG: 0,
  INFO: 1,
  WARN: 2,
  ERROR: 3,
  FATAL: 4,
};

const DEFAULT_CONFIG: LoggerConfig = {
  minLevel: 'DEBUG',
  maxLogs: 1000,
  maxBreadcrumbs: 100,
  consoleOutput: true,
  persistLogs: true,
  captureGlobalErrors: true,
  captureNetworkRequests: true,
};

const STORAGE_KEY = 'ceo_command_center_logs';
const BREADCRUMBS_KEY = 'ceo_command_center_breadcrumbs';
const SESSION_KEY = 'ceo_command_center_session';

// ============================================
// UTILITY FUNCTIONS
// ============================================

const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
};

const getSessionId = (): string => {
  let sessionId = sessionStorage.getItem(SESSION_KEY);
  if (!sessionId) {
    sessionId = `session-${generateId()}`;
    sessionStorage.setItem(SESSION_KEY, sessionId);
  }
  return sessionId;
};

const formatError = (error: unknown): LogEntry['error'] | undefined => {
  if (!error) return undefined;
  
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  
  if (typeof error === 'string') {
    return {
      name: 'Error',
      message: error,
    };
  }
  
  return {
    name: 'UnknownError',
    message: String(error),
  };
};

// ============================================
// LOGGER CLASS
// ============================================

class Logger {
  private config: LoggerConfig;
  private logs: LogEntry[] = [];
  private breadcrumbs: Breadcrumb[] = [];
  private activeSpans: Map<string, TraceSpan> = new Map();
  private sessionId: string;
  private correlationId: string | null = null;
  
  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this.sessionId = getSessionId();
    
    // Load persisted logs
    if (this.config.persistLogs) {
      this.loadFromStorage();
    }
    
    // Setup global error capture
    if (this.config.captureGlobalErrors) {
      this.setupGlobalErrorCapture();
    }
    
    // Setup network request capture
    if (this.config.captureNetworkRequests) {
      this.setupNetworkCapture();
    }
    
    // Log session start
    this.info('Logger', 'Session started', {
      sessionId: this.sessionId,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
    });
  }
  
  // ========== CONFIGURATION ==========
  
  setConfig(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }
  
  setCorrelationId(id: string | null): void {
    this.correlationId = id;
  }
  
  // ========== CORE LOGGING ==========
  
  private log(
    level: LogLevel,
    category: string,
    message: string,
    data?: Record<string, unknown>,
    error?: unknown
  ): LogEntry {
    // Check if we should log this level
    if (LOG_LEVELS[level] < LOG_LEVELS[this.config.minLevel]) {
      return {} as LogEntry;
    }
    
    const entry: LogEntry = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      level,
      category,
      message,
      data,
      error: formatError(error),
      correlationId: this.correlationId || undefined,
      sessionId: this.sessionId,
      tags: [],
    };
    
    // Add to logs
    this.logs.push(entry);
    
    // Trim if over limit
    if (this.logs.length > this.config.maxLogs) {
      this.logs = this.logs.slice(-this.config.maxLogs);
    }
    
    // Console output
    if (this.config.consoleOutput) {
      this.outputToConsole(entry);
    }
    
    // Persist
    if (this.config.persistLogs) {
      this.saveToStorage();
    }
    
    // Add breadcrumb for errors
    if (level === 'ERROR' || level === 'FATAL') {
      this.addBreadcrumb(category, message, data);
    }
    
    return entry;
  }
  
  debug(category: string, message: string, data?: Record<string, unknown>): LogEntry {
    return this.log('DEBUG', category, message, data);
  }
  
  info(category: string, message: string, data?: Record<string, unknown>): LogEntry {
    return this.log('INFO', category, message, data);
  }
  
  warn(category: string, message: string, data?: Record<string, unknown>): LogEntry {
    return this.log('WARN', category, message, data);
  }
  
  error(category: string, message: string, error?: unknown, data?: Record<string, unknown>): LogEntry {
    return this.log('ERROR', category, message, data, error);
  }
  
  fatal(category: string, message: string, error?: unknown, data?: Record<string, unknown>): LogEntry {
    return this.log('FATAL', category, message, data, error);
  }
  
  // ========== BREADCRUMBS ==========
  
  addBreadcrumb(category: string, message: string, data?: Record<string, unknown>): void {
    const breadcrumb: Breadcrumb = {
      timestamp: new Date().toISOString(),
      category,
      message,
      data,
    };
    
    this.breadcrumbs.push(breadcrumb);
    
    // Trim if over limit
    if (this.breadcrumbs.length > this.config.maxBreadcrumbs) {
      this.breadcrumbs = this.breadcrumbs.slice(-this.config.maxBreadcrumbs);
    }
  }
  
  getBreadcrumbs(): Breadcrumb[] {
    return [...this.breadcrumbs];
  }
  
  clearBreadcrumbs(): void {
    this.breadcrumbs = [];
  }
  
  // ========== PERFORMANCE TRACING ==========
  
  startSpan(name: string, category: string, data?: Record<string, unknown>): string {
    const spanId = generateId();
    const correlationId = this.correlationId || generateId();
    
    const span: TraceSpan = {
      id: spanId,
      name,
      category,
      startTime: performance.now(),
      correlationId,
      data,
      status: 'pending',
    };
    
    this.activeSpans.set(spanId, span);
    
    this.debug(category, `[SPAN START] ${name}`, { spanId, correlationId });
    
    return spanId;
  }
  
  endSpan(spanId: string, status: 'success' | 'error' = 'success', error?: string): TraceSpan | null {
    const span = this.activeSpans.get(spanId);
    if (!span) return null;
    
    span.endTime = performance.now();
    span.duration = span.endTime - span.startTime;
    span.status = status;
    span.error = error;
    
    this.activeSpans.delete(spanId);
    
    const logLevel = status === 'error' ? 'error' : 'debug';
    this[logLevel](
      span.category,
      `[SPAN END] ${span.name}`,
      status === 'error' ? new Error(error) : undefined,
      {
        spanId,
        correlationId: span.correlationId,
        duration: `${span.duration.toFixed(2)}ms`,
        status,
      }
    );
    
    return span;
  }
  
  async traceAsync<T>(
    name: string,
    category: string,
    fn: () => Promise<T>,
    data?: Record<string, unknown>
  ): Promise<T> {
    const spanId = this.startSpan(name, category, data);
    
    try {
      const result = await fn();
      this.endSpan(spanId, 'success');
      return result;
    } catch (error) {
      this.endSpan(spanId, 'error', error instanceof Error ? error.message : String(error));
      throw error;
    }
  }
  
  // ========== USER ACTIONS ==========
  
  logUserAction(action: string, data?: Record<string, unknown>): LogEntry {
    this.addBreadcrumb('UserAction', action, data);
    return this.info('UserAction', action, { ...data, userAction: action });
  }
  
  // ========== API CALLS ==========
  
  logApiCall(
    method: string,
    url: string,
    status: number,
    duration: number,
    error?: unknown
  ): LogEntry {
    const level: LogLevel = status >= 400 ? 'ERROR' : 'INFO';
    const message = `${method} ${url} - ${status}`;
    
    return this.log(level, 'API', message, {
      method,
      url,
      status,
      duration: `${duration}ms`,
    }, error);
  }
  
  // ========== STORAGE ==========
  
  private loadFromStorage(): void {
    try {
      const logsJson = localStorage.getItem(STORAGE_KEY);
      const breadcrumbsJson = localStorage.getItem(BREADCRUMBS_KEY);
      
      if (logsJson) {
        this.logs = JSON.parse(logsJson);
      }
      if (breadcrumbsJson) {
        this.breadcrumbs = JSON.parse(breadcrumbsJson);
      }
    } catch (e) {
      console.warn('Failed to load logs from storage:', e);
    }
  }
  
  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.logs));
      localStorage.setItem(BREADCRUMBS_KEY, JSON.stringify(this.breadcrumbs));
    } catch (e) {
      console.warn('Failed to save logs to storage:', e);
    }
  }
  
  // ========== GLOBAL ERROR CAPTURE ==========
  
  private setupGlobalErrorCapture(): void {
    // Unhandled errors
    window.addEventListener('error', (event) => {
      this.error('GlobalError', event.message, event.error, {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      });
    });
    
    // Unhandled promise rejections
    window.addEventListener('unhandledrejection', (event) => {
      this.error('UnhandledRejection', 'Unhandled Promise Rejection', event.reason, {
        type: 'unhandledrejection',
      });
    });
  }
  
  // ========== NETWORK CAPTURE ==========
  
  private setupNetworkCapture(): void {
    // Intercept fetch
    const originalFetch = window.fetch;
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const method = init?.method || 'GET';
      const startTime = performance.now();
      
      try {
        const response = await originalFetch(input, init);
        const duration = performance.now() - startTime;
        
        // Only log API calls, not assets
        if (url.includes('/api/') || url.includes('airtable.com')) {
          this.logApiCall(method, url, response.status, duration);
        }
        
        return response;
      } catch (error) {
        const duration = performance.now() - startTime;
        this.logApiCall(method, url, 0, duration, error);
        throw error;
      }
    };
  }
  
  // ========== CONSOLE OUTPUT ==========
  
  private outputToConsole(entry: LogEntry): void {
    const styles: Record<LogLevel, string> = {
      DEBUG: 'color: #9CA3AF',
      INFO: 'color: #3B82F6',
      WARN: 'color: #F59E0B; font-weight: bold',
      ERROR: 'color: #EF4444; font-weight: bold',
      FATAL: 'color: #DC2626; font-weight: bold; background: #FEE2E2',
    };
    
    const prefix = `[${entry.level}] [${entry.category}]`;
    const style = styles[entry.level];
    
    if (entry.error) {
      console.groupCollapsed(`%c${prefix} ${entry.message}`, style);
      console.log('Data:', entry.data);
      console.error('Error:', entry.error);
      console.log('Breadcrumbs:', this.getBreadcrumbs().slice(-10));
      console.groupEnd();
    } else {
      console.log(`%c${prefix} ${entry.message}`, style, entry.data || '');
    }
  }
  
  // ========== LOG RETRIEVAL ==========
  
  getLogs(options?: {
    level?: LogLevel;
    category?: string;
    since?: Date;
    correlationId?: string;
    limit?: number;
  }): LogEntry[] {
    let filtered = [...this.logs];
    
    if (options?.level) {
      const minLevel = LOG_LEVELS[options.level];
      filtered = filtered.filter(log => LOG_LEVELS[log.level] >= minLevel);
    }
    
    if (options?.category) {
      filtered = filtered.filter(log => log.category === options.category);
    }
    
    if (options?.since) {
      const sinceTime = options.since.getTime();
      filtered = filtered.filter(log => new Date(log.timestamp).getTime() >= sinceTime);
    }
    
    if (options?.correlationId) {
      filtered = filtered.filter(log => log.correlationId === options.correlationId);
    }
    
    if (options?.limit) {
      filtered = filtered.slice(-options.limit);
    }
    
    return filtered;
  }
  
  getErrors(limit = 50): LogEntry[] {
    return this.getLogs({ level: 'ERROR', limit });
  }
  
  getSessionLogs(): LogEntry[] {
    return this.logs.filter(log => log.sessionId === this.sessionId);
  }
  
  // ========== EXPORT ==========
  
  exportLogs(format: 'json' | 'txt' = 'json'): string {
    const exportData = {
      exportedAt: new Date().toISOString(),
      sessionId: this.sessionId,
      totalLogs: this.logs.length,
      breadcrumbs: this.breadcrumbs,
      logs: this.logs,
      systemInfo: {
        userAgent: navigator.userAgent,
        url: window.location.href,
        screenSize: `${window.screen.width}x${window.screen.height}`,
        language: navigator.language,
      },
    };
    
    if (format === 'json') {
      return JSON.stringify(exportData, null, 2);
    }
    
    // Text format
    let txt = `CEO Command Center - Log Export\n`;
    txt += `=================================\n`;
    txt += `Exported: ${exportData.exportedAt}\n`;
    txt += `Session: ${exportData.sessionId}\n`;
    txt += `URL: ${exportData.systemInfo.url}\n`;
    txt += `User Agent: ${exportData.systemInfo.userAgent}\n`;
    txt += `\n`;
    txt += `LOGS (${exportData.totalLogs}):\n`;
    txt += `-----------------------------------\n`;
    
    this.logs.forEach(log => {
      txt += `[${log.timestamp}] [${log.level}] [${log.category}] ${log.message}\n`;
      if (log.data) {
        txt += `  Data: ${JSON.stringify(log.data)}\n`;
      }
      if (log.error) {
        txt += `  Error: ${log.error.name}: ${log.error.message}\n`;
        if (log.error.stack) {
          txt += `  Stack: ${log.error.stack}\n`;
        }
      }
    });
    
    txt += `\nBREADCRUMBS (${exportData.breadcrumbs.length}):\n`;
    txt += `-----------------------------------\n`;
    this.breadcrumbs.forEach(b => {
      txt += `[${b.timestamp}] [${b.category}] ${b.message}\n`;
    });
    
    return txt;
  }
  
  downloadLogs(format: 'json' | 'txt' = 'json'): void {
    const content = this.exportLogs(format);
    const blob = new Blob([content], { type: format === 'json' ? 'application/json' : 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const filename = `ceo-command-center-logs-${timestamp}.${format}`;
    
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    this.info('Logger', `Logs exported to ${filename}`);
  }
  
  // ========== CLEAR ==========
  
  clearLogs(): void {
    this.logs = [];
    this.breadcrumbs = [];
    
    if (this.config.persistLogs) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(BREADCRUMBS_KEY);
    }
    
    this.info('Logger', 'Logs cleared');
  }
  
  // ========== STATS ==========
  
  getStats(): {
    totalLogs: number;
    byLevel: Record<LogLevel, number>;
    byCategory: Record<string, number>;
    errorRate: number;
    sessionDuration: number;
  } {
    const byLevel: Record<LogLevel, number> = {
      DEBUG: 0,
      INFO: 0,
      WARN: 0,
      ERROR: 0,
      FATAL: 0,
    };
    
    const byCategory: Record<string, number> = {};
    
    this.logs.forEach(log => {
      byLevel[log.level]++;
      byCategory[log.category] = (byCategory[log.category] || 0) + 1;
    });
    
    const errorCount = byLevel.ERROR + byLevel.FATAL;
    const errorRate = this.logs.length > 0 ? (errorCount / this.logs.length) * 100 : 0;
    
    const sessionStart = this.logs.find(l => l.sessionId === this.sessionId)?.timestamp;
    const sessionDuration = sessionStart 
      ? Date.now() - new Date(sessionStart).getTime()
      : 0;
    
    return {
      totalLogs: this.logs.length,
      byLevel,
      byCategory,
      errorRate,
      sessionDuration,
    };
  }
}

// ============================================
// SINGLETON INSTANCE
// ============================================

export const logger = new Logger();

// ============================================
// CONVENIENCE EXPORTS
// ============================================

export const log = {
  debug: (category: string, message: string, data?: Record<string, unknown>) => 
    logger.debug(category, message, data),
  info: (category: string, message: string, data?: Record<string, unknown>) => 
    logger.info(category, message, data),
  warn: (category: string, message: string, data?: Record<string, unknown>) => 
    logger.warn(category, message, data),
  error: (category: string, message: string, error?: unknown, data?: Record<string, unknown>) => 
    logger.error(category, message, error, data),
  fatal: (category: string, message: string, error?: unknown, data?: Record<string, unknown>) => 
    logger.fatal(category, message, error, data),
  action: (action: string, data?: Record<string, unknown>) => 
    logger.logUserAction(action, data),
  api: (method: string, url: string, status: number, duration: number, error?: unknown) => 
    logger.logApiCall(method, url, status, duration, error),
  trace: <T>(name: string, category: string, fn: () => Promise<T>, data?: Record<string, unknown>) => 
    logger.traceAsync(name, category, fn, data),
  startSpan: (name: string, category: string, data?: Record<string, unknown>) => 
    logger.startSpan(name, category, data),
  endSpan: (spanId: string, status?: 'success' | 'error', error?: string) => 
    logger.endSpan(spanId, status, error),
  breadcrumb: (category: string, message: string, data?: Record<string, unknown>) => 
    logger.addBreadcrumb(category, message, data),
};

export default logger;
